import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { WorkflowFilter, WorkflowFilterCondition } from "../types";
import MappingInput from "./MappingInput";

// Make's filter operators (FilterEvaluator::OPERATORS in the cabinet), grouped.
export const OPERATOR_GROUPS: Array<{ group: string; operators: string[] }> = [
    { group: 'basic', operators: ['exist', 'notexist'] },
    { group: 'text', operators: ['text:equal', 'text:equal:ci', 'text:notequal', 'text:notequal:ci', 'text:contain', 'text:contain:ci', 'text:notcontain', 'text:notcontain:ci', 'text:startwith', 'text:startwith:ci', 'text:notstartwith', 'text:endwith', 'text:endwith:ci', 'text:notendwith', 'text:pattern', 'text:pattern:ci', 'text:notpattern'] },
    { group: 'number', operators: ['number:equal', 'number:notequal', 'number:greater', 'number:less', 'number:greaterorequal', 'number:lessorequal'] },
    { group: 'date', operators: ['date:equal', 'date:notequal', 'date:greater', 'date:less', 'date:greaterorequal', 'date:lessorequal'] },
    { group: 'array', operators: ['array:contain', 'array:contain:ci', 'array:notcontain', 'array:length:equal', 'array:length:notequal', 'array:length:greater', 'array:length:less'] },
    { group: 'boolean', operators: ['boolean:equal', 'boolean:notequal'] },
];

const UNARY = ['exist', 'notexist'];

interface FilterEditorProps {
    moduleId: number | null;
    filter: WorkflowFilter | null | undefined;
    readOnly: boolean;
    onSave: (filter: WorkflowFilter | null) => void;
    onClose: () => void;
}

/** Conditions on a link: OR between groups, AND inside a group. */
const FilterEditor = ({ moduleId, filter, readOnly, onSave, onClose }: FilterEditorProps) => {
    const { t } = useTranslation(['workflow']);
    const [name, setName] = useState('');
    const [groups, setGroups] = useState<WorkflowFilterCondition[][]>([]);

    useEffect(() => {
        setName(filter?.name || '');
        setGroups(filter?.conditions && filter.conditions.length > 0 ? filter.conditions : [[{ a: '', o: 'text:equal', b: '' }]]);
    }, [filter, moduleId]);

    const setCondition = (g: number, c: number, patch: Partial<WorkflowFilterCondition>) =>
        setGroups(groups.map((group, gi) => (gi !== g ? group : group.map((cond, ci) => (ci === c ? { ...cond, ...patch } : cond)))));

    const removeCondition = (g: number, c: number) =>
        setGroups(groups.map((group, gi) => (gi !== g ? group : group.filter((_, ci) => ci !== c))).filter((group) => group.length > 0));

    const save = () => {
        const conditions = groups.map((group) => group.filter((c) => c.a !== '' || UNARY.includes(c.o))).filter((group) => group.length > 0);
        onSave(conditions.length === 0 ? null : { name: name || undefined, conditions });
    };

    return (
        <Modal show={moduleId !== null} onHide={onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t('workflow:filterTitle', { id: moduleId })}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <fieldset disabled={readOnly}>
                    <div className="mb-3">
                        <label className="form-label">{t('workflow:filterName')}</label>
                        <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
                    </div>
                    {groups.map((group, g) => (
                        <div key={g}>
                            {g > 0 && <div className="text-center fs-13 fw-semibold text-uppercase text-muted my-3">{t('workflow:or')}</div>}
                            <div className="border border-dashed rounded p-3">
                                {group.map((cond, c) => (
                                    <div key={c}>
                                        {c > 0 && <div className="fs-13 fw-semibold text-uppercase text-muted my-2">{t('workflow:and')}</div>}
                                        <div className="row g-2 align-items-start">
                                            <div className="col-md-5"><MappingInput value={cond.a} onChange={(v) => setCondition(g, c, { a: v })} placeholder="{{1.text}}" /></div>
                                            <div className="col-md-3">
                                                <select className="form-select" value={cond.o} onChange={(e) => setCondition(g, c, { o: e.target.value })}>
                                                    {OPERATOR_GROUPS.map((og) => (
                                                        <optgroup key={og.group} label={t('workflow:operatorGroup_' + og.group)}>
                                                            {og.operators.map((o) => <option key={o} value={o}>{t('workflow:op_' + o.replace(/:/g, '_'), { defaultValue: o })}</option>)}
                                                        </optgroup>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-3">
                                                {!UNARY.includes(cond.o) && <MappingInput value={cond.b ?? ''} onChange={(v) => setCondition(g, c, { b: v })} />}
                                            </div>
                                            <div className="col-md-1 text-end">
                                                <button type="button" className="btn btn-soft-danger btn-icon" onClick={() => removeCondition(g, c)}><i className="ri-close-line" /></button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <button type="button" className="btn btn-sm btn-soft-primary mt-3" onClick={() => setGroups(groups.map((gr, gi) => (gi === g ? [...gr, { a: '', o: 'text:equal', b: '' }] : gr)))}>
                                    <i className="ri-add-line align-bottom me-1" />{t('workflow:addAnd')}
                                </button>
                            </div>
                        </div>
                    ))}
                    <button type="button" className="btn btn-sm btn-soft-secondary mt-3" onClick={() => setGroups([...groups, [{ a: '', o: 'text:equal', b: '' }]])}>
                        <i className="ri-add-line align-bottom me-1" />{t('workflow:addOr')}
                    </button>
                </fieldset>
            </Modal.Body>
            <Modal.Footer>
                {!readOnly && filter && <button type="button" className="btn btn-soft-danger me-auto" onClick={() => onSave(null)}>{t('workflow:removeFilter')}</button>}
                <button type="button" className="btn btn-light" onClick={onClose}>{t('workflow:cancel')}</button>
                {!readOnly && <button type="button" className="btn btn-primary" onClick={save}>{t('workflow:apply')}</button>}
            </Modal.Footer>
        </Modal>
    );
};

export default FilterEditor;
